import React from "react";

import {
  createAccordionItems,
  isFootprintReadOnly,
  useGetCarbonProgramByFarmId,
  useGetFootprintingFarmFootprint,
  useNavigate,
  useTranslation,
  useWasAutofilled,
} from "utils";

import { FormFlowScreen, FormPage } from "components";

import { AutofillBanner } from "pages/app/footprinting/components/AutofillBanner";
import { FarmElectricityForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmElectricityForm";
import { FarmFuelForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmFuelForm";
import { FarmGettingStartedForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmGettingStartedForm";
import { FarmLandUseForm } from "pages/app/footprinting/components/formFlows/farmFlow/FarmLandUseForm";
import { FarmCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface FarmFlowProps {
  farmId: string;
  year: number;
  initialStep?: number;
  editMode?: boolean;
  enablePageTransitions?: boolean;
}

export const FarmFormFlow = ({ farmId, year, initialStep, editMode, enablePageTransitions }: FarmFlowProps) => {
  const { t } = useTranslation("footprintingFarm");
  const navigate = useNavigate();
  const farmProgressQuery = useGetFootprintingFarmFootprint(farmId, year);
  const readOnly = isFootprintReadOnly(farmProgressQuery.data);
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const wasAutofilled = useWasAutofilled("farm", farmId, year);
  const startYear = carbonProgramQuery.data?.farmLevelFootprinting?.startYear;
  const isStartYear = startYear === year;

  const getPageStatus = (pageKey: string) => {
    if (farmProgressQuery.data?.pagesCompleted.includes(pageKey)) return "complete";
    if (farmProgressQuery.data?.pagesIncomplete.includes(pageKey)) return "incomplete";
    return null;
  };

  const farmFlowSteps: FormPage[] = [
    {
      navigationTitle: t("footprinting:navigation.farm.gettingStarted"),
      key: FarmGettingStartedForm.Key,
      status: getPageStatus(FarmGettingStartedForm.Key),
      pageSubtitle: t("farmGettingStartedForm.subTitle"),
      body: () => <FarmGettingStartedForm primaryDataId={farmProgressQuery.data?.footprint?.id} />,
    },
    {
      navigationTitle: t("footprinting:navigation.farm.electricity"),
      key: FarmElectricityForm.Key,
      status: getPageStatus(FarmElectricityForm.Key),
      pageSubtitle: t("electricityForm.info"),
      body: () => <FarmElectricityForm readOnly={readOnly} primaryDataId={farmProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingFarm", "farmElectricity"),
      accordionFooter: wasAutofilled ? <AutofillBanner /> : undefined,
    },
    {
      navigationTitle: t("footprinting:navigation.farm.fuel"),
      key: FarmFuelForm.Key,
      status: getPageStatus(FarmFuelForm.Key),
      pageSubtitle: t("fuelForm.info"),
      body: () => <FarmFuelForm readOnly={readOnly} primaryDataId={farmProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingFarm", "farmFuel"),
    },
    {
      navigationTitle: t("footprinting:navigation.farm.landuse"),
      key: FarmLandUseForm.Key,
      status: getPageStatus(FarmLandUseForm.Key),
      pageSubtitle: isStartYear ? t("landUseForm.infoStartYear") : t("landUseForm.infoFollowingYear"),
      body: () => <FarmLandUseForm readOnly={readOnly} primaryDataId={farmProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingFarm", "farmLuc"),
    },
    {
      navigationTitle: t("footprinting:navigation.farm.comment"),
      key: FarmCommentForm.Key,
      status: getPageStatus(FarmCommentForm.Key),
      pageSubtitle: t("footprinting:commentForm.subTitle"),
      body: () => <FarmCommentForm readOnly={readOnly} primaryDataId={farmProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
  ];

  // Carbon programs can opt out of specific steps via excludedFormSteps. Excluded steps
  // are filtered here so they never render, and the server mirrors this by excluding
  // them from completion stats. To exclude a step, add its FormStepId to the carbon
  // program's excludedFormSteps array in consts/carbonPrograms.ts on the API.
  const excludedSteps = new Set(carbonProgramQuery.data?.farmLevelFootprinting.excludedFormSteps ?? []);
  const enabledSteps = farmFlowSteps.filter((step) => !excludedSteps.has(step.key));

  return (
    <FormFlowScreen
      formPages={enabledSteps}
      completionPercent={farmProgressQuery.data?.completionPct ?? 0}
      initialStep={initialStep}
      navBarTitle={t("footprinting:navigation.farm.navBarTitle", { year })}
      editMode={editMode}
      onClose={() => navigate("/footprinting", { state: { year } })}
      isLoading={farmProgressQuery.isLoading}
      enablePageTransitions={enablePageTransitions}
    />
  );
};
